// Pod-facing pieces: the journey bar and the queue rows.
//
// The hero card that used to live here went with the home screen it was built
// for — the operator home now leads with the day's score and carries its own,
// smaller pod card underneath it.

import type { JourneyStepVM, PodVM, WoState } from '@/api/types';
import { StatChip } from '@/components/home/Score';
import { PodMark } from '@/components/ui/Icon';
import { useT } from '@/i18n/useT';
import { podStateChip } from '@/lib/podState';

/**
 * How far along its route a pod is, as one bar.
 *
 * This was a segment per work order — 35 slivers a couple of pixels wide, with
 * labels clipped to a letter or two. It looked like a route and told you
 * nothing. A pod's progress is one number, so it gets one bar; the shape of the
 * route belongs in the graph behind it, which is what tapping opens.
 */
/** Work in progress reads before work merely ready to pick up. */
const rank = (s: JourneyStepVM) => (s.state === 'progress' ? 0 : 1);

export function JourneyProgress({
  steps,
  onOpen,
}: {
  steps: JourneyStepVM[];
  /** Open the full route graph. Without it the bar is a read-only display. */
  onOpen?: () => void;
}) {
  const t = useT();
  if (steps.length === 0) return null;

  const done = steps.filter((s) => s.status === 'done').length;
  const pct = Math.round((done / steps.length) * 100);

  // Where the pod actually is — which on a branching route is more than one
  // place at a time. Naming only the first open step said "Sanding Booth" while
  // the CNC, the tiling and the plumbing picklist were all live too, which is
  // the difference between a pod that has stalled and one being worked on from
  // four sides at once.
  const live = steps
    .filter((s) => s.state === 'progress' || s.state === 'ready')
    .sort((a, b) => rank(a) - rank(b) || a.sequence - b.sequence);
  const shown = live.slice(0, 2).map((s) => s.workcentreName);
  const more = live.length - shown.length;
  const where = live.length === 0
    ? done === steps.length
      ? t.routeAllDone
      : t.routeNothingLive
    : `${t.routeNowAt} ${shown.join(', ')}`;

  const body = (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 5 }}>
        <span
          style={{
            font: '600 11px Manrope,sans-serif',
            color: 'var(--sub)',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'left',
          }}
        >
          {where}
        </span>
        {/* Outside the truncating text on purpose: two long work centre names
            would push the count into the ellipsis, and an operator reading
            "Now at FL - Door & Jamb…" would take that for the whole story. */}
        {more > 0 && (
          <span style={{ flex: 'none', font: '600 11px Manrope,sans-serif', color: 'var(--sub)' }}>
            +{more}
          </span>
        )}
        <span
          className="numeric"
          style={{ marginLeft: 'auto', flex: 'none', font: "700 11px 'Space Grotesk',sans-serif" }}
        >
          {done} / {steps.length}
        </span>
        {onOpen && (
          <span aria-hidden="true" style={{ flex: 'none', color: 'var(--sub)' }}>
            ›
          </span>
        )}
      </div>
      <span
        aria-hidden="true"
        style={{
          display: 'block',
          height: 8,
          borderRadius: 4,
          background: 'var(--line)',
          overflow: 'hidden',
        }}
      >
        <span
          style={{
            display: 'block',
            height: '100%',
            width: `${pct}%`,
            borderRadius: 4,
            background: 'var(--accent)',
          }}
        />
      </span>
      <span className="sr-only">
        {done} of {steps.length} operations done
        {live.length ? `, now at ${live.map((s) => s.workcentreName).join(', ')}` : ''}.
      </span>
    </>
  );

  const style = { display: 'block', width: '100%' } as const;
  return onOpen ? (
    <button type="button" className="press-row" onClick={onOpen} style={style} aria-label={t.podJourney}>
      {body}
    </button>
  ) : (
    <div style={style}>{body}</div>
  );
}

export function stateLabel(state: PodVM['state']): string {
  switch (state) {
    case 'pending':
      return 'Waiting for another WO';
    case 'waiting':
      return 'Waiting for components';
    case 'ready':
      return 'Ready';
    case 'progress':
      return 'In Progress';
    case 'done':
      return 'Finished';
    case 'cancel':
      return 'Cancelled';
    default:
      return state;
  }
}

/**
 * The state a pod is in, as a chip on a list row.
 *
 * Every pod list wears this — the day on the Pods screen, the next-pod cards
 * on the home screen, the finder's results — so that "is it here yet, is
 * someone on it, is it done" is read off the list and never needs the pod
 * opened to find out. One component so the lists cannot drift apart in what
 * they call a state.
 */
export function PodStateChip({ state, started = false }: { state: WoState; started?: boolean }) {
  const t = useT();
  const chip = podStateChip(state, t, started);
  return (
    <StatChip tone={chip.tone} solid={chip.solid} size="sm">
      {chip.label}
    </StatChip>
  );
}

/** Is the pod physically here to be worked on? */
export function isActionable(state: PodVM['state']): boolean {
  return state === 'ready' || state === 'progress';
}

/**
 * Odoo refuses button_start unless the employee is permitted at the work
 * centre ("You are not allowed to work on the workorder"). Knowing that up
 * front lets the app say so instead of letting someone tap into a refusal.
 *
 * This is the PIN operator's rule. interpod_mrp_shopfloor (18.0.2.3.0+)
 * reports `all_employees_allowed` for anyone signed in as their own Odoo
 * account who is not in the Shopfloor Operator group, so a production manager
 * can start and sign off anywhere, and the job screen lets a named session
 * through before asking this at all.
 */
export function operatorMayStart(pod: PodVM, operatorId: number | null | undefined): boolean {
  if (pod.allEmployeesAllowed) return true;
  if (operatorId == null) return false;
  // An empty list means Odoo has not restricted this order.
  if (pod.allowedEmployeeIds.length === 0) return true;
  return pod.allowedEmployeeIds.includes(operatorId);
}

export function QueueRow({
  pod,
  active,
  tag,
  onClick,
}: {
  pod: PodVM;
  active: boolean;
  tag?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="press-row"
      onClick={onClick}
      aria-current={active ? 'true' : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        width: '100%',
        padding: '11px 12px',
        borderRadius: 13,
        background: active ? 'var(--soft)' : 'var(--surface)',
        border: `1.5px solid ${active ? 'var(--accent)' : 'var(--line)'}`,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 30,
          height: 30,
          borderRadius: 9,
          flex: 'none',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--surface2)',
          color: 'var(--sub)',
        }}
      >
        <PodMark size={17} />
      </span>
      <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <span style={{ display: 'block', font: "600 12.5px 'Space Grotesk',sans-serif" }}>{pod.mo}</span>
        <span
          style={{
            display: 'block',
            font: '500 10.5px Manrope,sans-serif',
            color: 'var(--sub)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {[pod.proj, pod.type, pod.spec[0]].filter(Boolean).join(' · ')}
        </span>
      </span>
      {tag ? (
        <span style={{ font: '600 10px Manrope,sans-serif', color: 'var(--sub)', flex: 'none' }}>{tag}</span>
      ) : (
        <PodStateChip state={pod.state} />
      )}
    </button>
  );
}
